import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useUiStore } from '../store/uiStore';
import { safeParseMetrics } from '../utils/metricsValidators';
import { measureCacheInjectionPerformance } from '../utils/metricsErrorHandling';

interface IncrementalUpdatesOptions {
  enabled?: boolean;
  userId?: string; // SECURITY: Required user ID for user-aware cache operations
}

/**
 * Hook to handle incremental cache updates from metrics_calculated events.
 * This listens for custom events dispatched by useGlobalSSE and processes them
 * using the hybrid cache manager for surgical cache updates.
 */
export const useIncrementalUpdates = (options: IncrementalUpdatesOptions = {}) => {
  const { enabled = true, userId } = options;
  const queryClient = useQueryClient();
  const { userTimezone } = useAuthStore();
  const { setDashboardUpdating } = useUiStore();
  
  // SECURITY: Validate that userId is provided for proper cache isolation
  if (enabled && !userId) {
    console.warn('⚠️ [useIncrementalUpdates] Hook enabled but no userId provided - disabling for security');
    return { enabled: false };
  }

  /**
   * Handle metrics calculated events for incremental cache updates
   */
  const handleMetricsCalculated = useCallback((event: Event) => {
    if (!enabled) return;

    const customEvent = event as CustomEvent;

    try {
      console.log(`🎯 [useIncrementalUpdates] Processing metrics_calculated event`);
      setDashboardUpdating(true, 'sse');

      const eventDetail = customEvent.detail;
      const rawMetricsData = eventDetail?.metrics;
      
      // Parse metrics data
      const metricsData = typeof rawMetricsData === 'string' 
        ? safeParseMetrics(rawMetricsData) 
        : rawMetricsData;
      
      if (!metricsData) {
        console.warn('❌ [useIncrementalUpdates] Invalid metrics data received');
        setDashboardUpdating(false, 'sse');
        return;
      }

      console.log(`🚀 [useIncrementalUpdates] Processing cache updates using hybrid strategy: ${metricsData.update_type || 'full_refresh'}`);
      
      // Get effective timezone (same logic as Dashboard)
      const effectiveTimezone = userTimezone || 'UTC';
      
      // Check if this is an incremental update
      if (metricsData.update_type === 'incremental' && metricsData.trade_data) {
        // ✅ Incremental updates using hybrid cache manager
        const tradeData = metricsData.trade_data;
        const newTimeframes = metricsData.pnl_timeframes; // Direct access to timeframes
        
        console.log(`🎯 [useIncrementalUpdates] Performing hybrid incremental update for ${tradeData.strategy}/${tradeData.account}: ${tradeData.pnl > 0 ? '+' : ''}${tradeData.pnl}`);
        console.log(`📊 [useIncrementalUpdates] Incremental update details:`, {
          account: tradeData.account,
          strategy: tradeData.strategy,
          pnl: tradeData.pnl,
          updateType: metricsData.update_type,
          hasTimeframes: !!newTimeframes,
          timeframes: newTimeframes
        });
        
        // Import and use hybrid cache manager (dynamic import to avoid circular deps)
        import('../utils/hybridCacheManager').then(({ performHybridCacheUpdate }) => {
          const result = measureCacheInjectionPerformance('Hybrid Cache Update', () => {
            return performHybridCacheUpdate(
              queryClient,
              tradeData,
              newTimeframes,
              effectiveTimezone,
              userId! // SECURITY: User ID validated above
            );
          });
          
          if (result.success) {
            setDashboardUpdating(false, 'sse');
            console.log(`✅ [useIncrementalUpdates] Hybrid cache update complete - incremental: ${result.incrementalSuccess}, invalidation: ${result.invalidationSuccess}`);
            
            // Notify that incremental update succeeded
            if (window.dispatchEvent) {
              window.dispatchEvent(new CustomEvent('incrementalUpdateSuccess', {
                detail: { 
                  account: tradeData.account,
                  strategy: tradeData.strategy,
                  pnl: tradeData.pnl,
                  incrementalSuccess: result.incrementalSuccess,
                  invalidationSuccess: result.invalidationSuccess,
                  operationsCompleted: result.operationsCompleted,
                  totalOperations: result.totalOperations,
                  timestamp: Date.now()
                }
              }));
            }
          } else {
            throw new Error(`Hybrid cache update failed: ${result.errors.join(', ')}`);
          }
        }).catch(error => {
          console.error('❌ [useIncrementalUpdates] Failed to load hybrid cache manager:', error);
          
          // Notify that hybrid update failed - fallback to invalidation
          if (window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('incrementalUpdateFailed', {
              detail: { 
                error: error.message,
                account: tradeData.account,
                strategy: tradeData.strategy,
                pnl: tradeData.pnl,
                timestamp: Date.now()
              }
            }));
          }
          
          // Fallback to full invalidation
          console.log('🔄 [useIncrementalUpdates] Falling back to full invalidation...');
          import('../utils/hybridCacheManager').then(({ fallbackToFullInvalidation }) => {
            const success = fallbackToFullInvalidation(queryClient, effectiveTimezone, userId!, 'incremental_update_failed');
            setDashboardUpdating(false, 'sse');
            
            if (success) {
              console.log('✅ [useIncrementalUpdates] Fallback invalidation completed successfully');
            } else {
              console.error('❌ [useIncrementalUpdates] Fallback invalidation also failed');
            }
          });
        });
      } else {
        // Full refresh - use fallback invalidation
        console.log('🔄 [useIncrementalUpdates] Processing full refresh via invalidation...');
        import('../utils/hybridCacheManager').then(({ fallbackToFullInvalidation }) => {
          const success = fallbackToFullInvalidation(queryClient, effectiveTimezone, userId!, 'full_refresh');
          setDashboardUpdating(false, 'sse');
          
          if (success) {
            console.log('✅ [useIncrementalUpdates] Full refresh invalidation completed successfully');
          } else {
            console.error('❌ [useIncrementalUpdates] Full refresh invalidation failed');
          }
        });
      }
    } catch (error) {
      console.error('❌ [useIncrementalUpdates] Error processing metrics calculated event:', error);
      setDashboardUpdating(false, 'sse');
    }
  }, [enabled, queryClient, userTimezone, setDashboardUpdating, userId]);

  // Set up event listener for metricsCalculated events
  useEffect(() => {
    if (!enabled) return;

    console.log('🎯 [useIncrementalUpdates] Setting up metricsCalculated event listener');
    
    window.addEventListener('metricsCalculated', handleMetricsCalculated);
    
    return () => {
      console.log('🎯 [useIncrementalUpdates] Removing metricsCalculated event listener');
      window.removeEventListener('metricsCalculated', handleMetricsCalculated);
    };
  }, [enabled, handleMetricsCalculated, userId]);

  return {
    enabled
  };
};